import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Timer, FileSpreadsheet } from "lucide-react";
import { api } from "../lib/api";
import { saveBlob } from "../lib/download";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { ST_COLORS } from "./VehicleStatus";

const exportXlsx = async (d) => {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  const add = (name, cols, rows) => {
    const ws = wb.addWorksheet(name, { views: [{ rightToLeft: true }] });
    ws.columns = cols.map(([h, w]) => ({ header: h, width: w }));
    ws.getRow(1).font = { bold: true };
    rows.forEach((r) => ws.addRow(r));
  };
  add("متوقفة حالياً", [["المركبة", 16], ["النوع", 14], ["الفرع", 14], ["الحالة", 22], ["منذ", 12], ["عدد الأيام", 11], ["السبب", 36], ["بواسطة", 18]],
    d.current.map((r) => [r.vehicle, r.type, r.branch, r.label, r.from, r.days, r.note, r.by]));
  add("سجل التوقف السابق", [["المركبة", 16], ["الحالة", 22], ["من", 12], ["إلى", 12], ["عدد الأيام", 11], ["السبب", 36], ["أُخرجت بواسطة", 18]],
    d.past.map((r) => [r.vehicle, r.label, r.from, r.to, r.days, r.note, r.exit_by]));
  const buf = await wb.xlsx.writeBuffer();
  await saveBlob(new Blob([buf]), `المركبات_المتوقفة_${new Date().toISOString().slice(0, 10)}.xlsx`);
};

const Table = ({ head, rows, testid }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200" data-testid={testid}>
    <table className="w-full text-xs"><thead className="bg-slate-50 text-slate-500"><tr>{head.map((h) => <th key={h} className="whitespace-nowrap p-2 text-right">{h}</th>)}</tr></thead>
      <tbody>{rows}</tbody></table>
  </div>
);

export const VehicleDowntimeReport = () => {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [f, setF] = useState("all");
  useEffect(() => { if (open) { setD(null); api.vehicleDowntime().then(setD).catch(() => toast.error("تعذّر تحميل التقرير")); } }, [open]);
  const cur = (d?.current || []).filter((r) => f === "all" || r.key === f);
  const past = (d?.past || []).filter((r) => f === "all" || r.key === f);
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-1.5" data-testid="vehicle-downtime-open"><Timer className="h-4 w-4" /> المركبات المتوقفة</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="max-h-[90vh] max-w-4xl overflow-y-auto" data-testid="vehicle-downtime-dialog">
          <DialogHeader className="pe-6 text-right"><DialogTitle className="font-heading">تقرير المركبات المتوقفة</DialogTitle>
            <DialogDescription>كم يوماً بقيت كل مركبة في الصيانة أو الورشة أو خارج الخدمة</DialogDescription></DialogHeader>
          {!d ? <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div className="rounded-xl bg-orange-50 p-3"><p className="text-[11px] text-orange-700">متوقفة الآن</p><p className="font-heading text-2xl font-bold text-orange-700" data-testid="downtime-current-count">{d.current.length}</p></div>
                <div className="rounded-xl bg-red-50 p-3"><p className="text-[11px] text-red-700">أكثر من 7 أيام</p><p className="font-heading text-2xl font-bold text-red-700">{d.current.filter((r) => r.days > 7).length}</p></div>
                <div className="rounded-xl bg-slate-100 p-3"><p className="text-[11px] text-slate-600">أطول توقف حالي</p><p className="font-heading text-2xl font-bold">{d.current[0]?.days ?? 0} يوم</p></div>
                <div className="rounded-xl bg-emerald-50 p-3"><p className="text-[11px] text-emerald-700">متوسط مدة التوقف السابقة</p><p className="font-heading text-2xl font-bold text-emerald-700" data-testid="downtime-avg">{d.avg_past_days} يوم</p></div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <button onClick={() => setF("all")} className={`rounded-full border px-3 py-1 text-xs ${f === "all" ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200"}`} data-testid="downtime-filter-all">الكل</button>
                {d.statuses.map((s) => (
                  <button key={s.key} onClick={() => setF(s.key)} data-testid={`downtime-filter-${s.key}`}
                    className={`rounded-full border px-3 py-1 text-xs ${f === s.key ? "ring-2 ring-slate-700" : ""} ${ST_COLORS[s.color]}`}>{s.label} ({d.current.filter((r) => r.key === s.key).length})</button>))}
                <Button size="sm" variant="outline" className="ms-auto gap-1" onClick={() => exportXlsx({ current: cur, past })} data-testid="downtime-export"><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
              </div>
              <p className="text-sm font-bold">متوقفة حالياً ({cur.length})</p>
              <Table testid="downtime-current-table" head={["المركبة", "الحالة", "منذ", "الأيام", "السبب", "بواسطة"]} rows={cur.length ? cur.map((r) => (
                <tr key={r.asset_id} className="border-t border-slate-100">
                  <td className="whitespace-nowrap p-2 font-bold" dir="ltr">{r.vehicle}</td>
                  <td className="p-2"><span className={`rounded-full border px-2 py-0.5 ${ST_COLORS[r.color] || ST_COLORS.slate}`}>{r.label}</span></td>
                  <td className="whitespace-nowrap p-2">{r.from}</td>
                  <td className={`p-2 font-bold ${r.days > 7 ? "text-red-600" : "text-slate-700"}`}>{r.days}</td>
                  <td className="min-w-40 p-2 text-slate-500">{r.note || "—"}{r.returned_from ? ` · استُلمت من ${r.returned_from}` : ""}</td>
                  <td className="whitespace-nowrap p-2 text-slate-500">{r.by}</td></tr>))
                : <tr><td colSpan={6} className="p-6 text-center text-slate-400">لا توجد مركبات متوقفة</td></tr>} />
              <p className="text-sm font-bold">سجل التوقف السابق ({past.length})</p>
              <Table testid="downtime-past-table" head={["المركبة", "الحالة", "من", "إلى", "الأيام", "السبب"]} rows={past.length ? past.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="whitespace-nowrap p-2 font-bold" dir="ltr">{r.vehicle}</td><td className="p-2">{r.label}</td>
                  <td className="whitespace-nowrap p-2">{r.from}</td><td className="whitespace-nowrap p-2">{r.to}</td>
                  <td className="p-2 font-bold">{r.days}</td><td className="min-w-40 p-2 text-slate-500">{r.note || "—"}</td></tr>))
                : <tr><td colSpan={6} className="p-6 text-center text-slate-400">لا يوجد سجل بعد</td></tr>} />
            </div>)}
        </DialogContent>
      </Dialog>
    </>
  );
};
